import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    computed,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { Chart, Options } from 'highcharts/highstock';
import { isEqual } from 'lodash';
import { IRichBlockChart, RichBlockChartKind } from '@pure1/data';
import { ToastService } from '@pure/hive';

import { renderRichBlockText } from '../../../model/rich-block-text';

/** Every form the contract defines, in the order the switcher offers them. */
const ALL_KINDS: readonly RichBlockChartKind[] = ['bar', 'line', 'pie'];

/** Contract form → Highcharts series type. `bar` is a vertical column, not Highcharts' `bar`. */
const HIGHCHARTS_TYPE: Record<RichBlockChartKind, string> = {
    bar: 'column',
    line: 'line',
    pie: 'pie',
};

/**
 * A chart block (R2.3), with the alternative forms its own data supports (FR-046).
 *
 * The model names one form. Which others are offered is derived from the data's shape and never
 * from a field the model supplies — a model that asked for a pie over three series would
 * otherwise get an unrenderable chart, and the validator already rejects that case server-side.
 * Switching is local: no request, no chat turn.
 */
@Component({
    selector: 'copilot-rich-block-chart',
    templateUrl: './chart-block.component.html',
    styleUrl: './chart-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
})
export class ChartBlockComponent implements AfterViewInit, OnDestroy {
    private readonly zone = inject(NgZone);
    private readonly toast = inject(ToastService);
    private readonly container = viewChild.required<ElementRef<HTMLElement>>('chartContainer');

    private chart: Chart | null = null;
    private readonly viewReady = signal(false);

    readonly block = input.required<IRichBlockChart>();

    /** The form on screen. Starts at the model's choice and changes only by the switcher. */
    private readonly chosen = signal<RichBlockChartKind | null>(null);

    /**
     * The form on screen: the reader's choice when this block can take it, the model's otherwise.
     *
     * The guard is not defensive tidying. One component instance outlives the block it shows --
     * `setVariation` hands it another one -- and a `pie` chosen for a single-series block would
     * otherwise survive into a multi-series one, which draws `series[0]` alone and silently
     * answers a different question. No switcher button would read as active either, because none
     * of the offered forms would match.
     */
    readonly kind = computed<RichBlockChartKind>(() => {
        const chosen = this.chosen();
        return chosen !== null && this.availableKinds().includes(chosen) ? chosen : this.block().chart;
    });

    /**
     * The forms this data can actually take.
     *
     * - `bar` always: any series of values can be columns.
     * - `line` needs at least two points, or it draws a single dot and reads as a mistake.
     * - `pie` needs exactly one series and no negative value, because a slice is a share of a
     *   total — a negative has no share, and two series have no single total.
     */
    readonly availableKinds = computed<RichBlockChartKind[]>(() => {
        const block = this.block();
        const values = block.series.flatMap(s => s.values);
        const points = Math.min(...block.series.map(s => s.values.length));

        return ALL_KINDS.filter(kind => {
            if (kind === 'line') {
                return points >= 2;
            }
            if (kind === 'pie') {
                return block.series.length === 1 && values.every(v => v >= 0);
            }
            return true;
        });
    });

    /** One form is no choice, so the switcher stays hidden rather than rendering a single button. */
    readonly showSwitcher = computed(() => this.availableKinds().length > 1);

    /**
     * FR-047: the block's own copy, yielding the same markdown the answer-level copy uses.
     *
     * The data, not the picture. There is no copying a canvas into a message, and the numbers are
     * what survives a paste into a ticket -- which is also why the chosen FORM is deliberately not
     * part of it: `renderRichBlockText` renders categories and series, so a reader who switched to
     * a pie and copied gets the same text as one who did not. Matching the answer-level copy is
     * the point; a per-form variant would make the two disagree.
     */
    copy(): void {
        navigator.clipboard
            .writeText(renderRichBlockText(this.block()))
            .then(() => this.toast.addToast({ message: 'Chart copied', severity: 'default' }))
            .catch((error: unknown) => {
                this.toast.addToast({ message: 'Failed to copy chart', severity: 'major' });
                console.error('Failed to copy chart to clipboard', error);
            });
    }

    /**
     * What the chart is drawn from, by value rather than by identity.
     *
     * The conversation is `cloneDeep`d on every streamed chunk, so `block()` is a new object each
     * time while its contents are unchanged. Depending on that identity tore down and rebuilt
     * every chart in the conversation per chunk -- visibly, since a Highcharts rebuild reflows and
     * replays its entry animation. Structural equality makes a redraw mean the data or the form
     * actually changed.
     */
    private readonly drawSpec = computed(() => ({ block: this.block(), kind: this.kind() }), {
        equal: (a, b) => a.kind === b.kind && isEqual(a.block, b.block),
    });

    constructor() {
        effect(() => {
            // Depend on both so the chart is rebuilt when the data or form changes, or when the
            // view finally exists.
            const spec = this.drawSpec();
            if (!this.viewReady()) {
                return;
            }
            this.draw(spec.block, spec.kind);
        });
    }

    ngAfterViewInit(): void {
        this.viewReady.set(true);
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
        this.chart = null;
    }

    select(kind: RichBlockChartKind): void {
        this.chosen.set(kind);
    }

    label(kind: RichBlockChartKind): string {
        return kind.charAt(0).toUpperCase() + kind.slice(1);
    }

    private draw(block: IRichBlockChart, kind: RichBlockChartKind): void {
        const element = this.container().nativeElement;
        this.chart?.destroy();

        // Outside Angular: Highcharts installs its own listeners, and leaving them inside the
        // zone turns every tooltip hover into a change-detection pass over the conversation.
        this.zone.runOutsideAngular(() => {
            this.chart = new Chart(element, this.options(block, kind));
        });
    }

    private options(block: IRichBlockChart, kind: RichBlockChartKind): Options {
        const type = HIGHCHARTS_TYPE[kind];
        const categories = block.x.values.map(String);

        return {
            chart: { type, animation: false, height: 220, backgroundColor: 'transparent' },
            credits: { enabled: false },
            // The block's own title renders in the template, so Highcharts must not draw a second.
            title: { text: null },
            // One series needs no legend; its name is the axis label or the block title.
            legend: { enabled: block.series.length > 1 && kind !== 'pie' },
            xAxis: kind === 'pie' ? {} : { categories, title: { text: block.x.label ?? null } },
            yAxis:
                kind === 'pie'
                    ? {}
                    : {
                          title: { text: this.axisTitle(block) },
                      },
            tooltip: { valueSuffix: block.y?.unit ? ` ${block.y.unit}` : undefined },
            plotOptions: { pie: { dataLabels: { enabled: true } } },
            series:
                kind === 'pie'
                    ? [
                          {
                              type: 'pie',
                              name: block.series[0]?.label,
                              data: categories.map((category, index) => ({
                                  name: category,
                                  y: block.series[0]?.values[index],
                              })),
                          },
                      ]
                    : block.series.map(series => ({
                          type: type as 'column' | 'line',
                          name: series.label,
                          data: series.values,
                      })),
        } as Options;
    }

    /** The y label and its unit read as one thing: "Volumes (count)". */
    private axisTitle(block: IRichBlockChart): string | null {
        const label = block.y?.label;
        const unit = block.y?.unit;
        if (label && unit) {
            return `${label} (${unit})`;
        }
        return label ?? unit ?? null;
    }
}
